// Tipos de domínio — Fase 1 (fundação)
// Origem rastreável em todo dado que pode vir de fora (seção 5 do plano).

export type PapelMembro =
  | 'medico_titular'
  | 'medico_colaborador'
  | 'secretaria_pessoal'
  | 'secretaria_clinica'

export type TipoClinica = 'consultorio_proprio' | 'externa' | 'clinica360'

export interface PerfilUsuario {
  user_id: string
  nome: string
  email: string
  telefone: string | null
  crm: string | null
  especialidades: string[]
}

export interface Workspace {
  id: string
  nome: string
  medico_titular: string
  status: string
}

export interface WorkspaceMembro {
  id: string
  workspace_id: string
  user_id: string | null
  papel: PapelMembro
  clinica_scope_id: string | null
  permissoes: Record<string, boolean>
  convite_email: string | null
  convite_status: 'pendente' | 'aceito' | 'revogado'
  ativo: boolean
}

export interface Clinica {
  id: string
  workspace_id: string
  nome: string
  tipo: TipoClinica
  cor: string | null
  ativo: boolean
}

/** Vínculo do usuário logado com um workspace (membership + nome do workspace). */
export interface MeuWorkspace {
  workspace: Workspace
  membro: WorkspaceMembro
}

export const PAPEL_LABEL: Record<PapelMembro, string> = {
  medico_titular: 'Médico titular',
  medico_colaborador: 'Médico colaborador',
  secretaria_pessoal: 'Secretária pessoal',
  secretaria_clinica: 'Secretária de clínica',
}

export const TIPO_CLINICA_LABEL: Record<TipoClinica, string> = {
  consultorio_proprio: 'Consultório próprio',
  externa: 'Clínica externa',
  clinica360: 'Clínica 360 (integração)',
}

// ---------- Fase 2: pacientes, consultas, timeline ----------

export type OrigemRegistro = 'manual' | 'importacao' | 'convite' | 'clinica360' | 'diagnostico360' | 'paciente360'
export type SituacaoPaciente = 'ativa' | 'inativa'
export type TipoConsulta = 'primeira' | 'retorno'
export type SituacaoConsulta = 'agendada' | 'confirmada' | 'realizada' | 'cancelada' | 'faltou'
export type StatusProntidao = 'nao_iniciada' | 'pendente' | 'parcialmente_pronta' | 'pronta' | 'bloqueada'

export interface Paciente {
  id: string
  workspace_id: string
  nome: string
  cpf: string | null
  data_nasc: string | null
  genero: string | null
  email: string | null
  telefone: string | null
  observacoes: string | null
  origem: OrigemRegistro
  situacao: SituacaoPaciente
  proxima_acao: string | null
  created_at: string
}

export interface Consulta {
  id: string
  workspace_id: string
  paciente_id: string
  clinica_id: string | null
  data: string
  hora_inicio: string
  hora_fim: string | null
  tipo: TipoConsulta
  motivo: string | null
  observacoes: string | null
  situacao: SituacaoConsulta
  status_prontidao: StatusProntidao
  origem: string
}

export interface TimelineEvento {
  id: string
  paciente_id: string
  tipo: string
  titulo: string
  descricao: string | null
  autor_user_id: string | null
  consulta_id: string | null
  dados: Record<string, unknown>
  visibilidade: 'equipe' | 'medicos'
  created_at: string
}

export const ORIGEM_LABEL: Record<OrigemRegistro, string> = {
  manual: 'Cadastro manual',
  importacao: 'Importação',
  convite: 'Convite',
  clinica360: 'Clínica 360',
  diagnostico360: 'Diagnóstico 360',
  paciente360: 'Paciente 360',
}

export const TIPO_CONSULTA_LABEL: Record<TipoConsulta, string> = {
  primeira: 'Primeira consulta',
  retorno: 'Retorno',
}

export const SITUACAO_CONSULTA_LABEL: Record<SituacaoConsulta, string> = {
  agendada: 'Agendada',
  confirmada: 'Confirmada',
  realizada: 'Realizada',
  cancelada: 'Cancelada',
  faltou: 'Faltou',
}

export const PRONTIDAO_LABEL: Record<StatusProntidao, string> = {
  nao_iniciada: 'Preparação não iniciada',
  pendente: 'Preparação pendente',
  parcialmente_pronta: 'Parcialmente pronta',
  pronta: 'Pronta',
  bloqueada: 'Bloqueada',
}

// ---------- Fase 3: pendências, delegação, notificações ----------

export type CategoriaTarefa =
  | 'analisar_exame' | 'responder_paciente' | 'concluir_evolucao' | 'revisar_documento'
  | 'emitir_relatorio' | 'verificar_resultado' | 'autorizar_receita' | 'definir_conduta'
  | 'acompanhar_tratamento' | 'solicitar_contato' | 'agendar_retorno' | 'conferir_documentacao'
  | 'revisar_termo' | 'acompanhar_sintoma' | 'administrativa' | 'personalizada'

export type PrioridadeTarefa = 'baixa' | 'normal' | 'alta' | 'urgente'

export type StatusTarefa =
  | 'nova' | 'em_analise' | 'aguardando_medico' | 'aguardando_secretaria'
  | 'aguardando_paciente' | 'aguardando_exame' | 'programada' | 'concluida' | 'cancelada'

export interface Tarefa {
  id: string
  workspace_id: string
  titulo: string
  descricao: string | null
  paciente_id: string | null
  consulta_id: string | null
  categoria: CategoriaTarefa
  responsavel_user_id: string | null
  criado_por: string | null
  prioridade: PrioridadeTarefa
  prazo: string | null
  status: StatusTarefa
  motivo_cancelamento: string | null
  etiquetas: string[]
  visibilidade: 'equipe' | 'medicos'
  concluida_em: string | null
  created_at: string
}

export interface TarefaComentario {
  id: string
  tarefa_id: string
  autor_user_id: string | null
  texto: string
  sistema: boolean
  created_at: string
}

export interface TarefaDelegacao {
  id: string
  tarefa_id: string
  de_user_id: string | null
  para_user_id: string
  instrucao: string | null
  prazo: string | null
  status: 'ativa' | 'concluida' | 'devolvida' | 'cancelada'
  created_at: string
}

export interface Notificacao {
  id: string
  workspace_id: string
  user_id: string
  tipo: string
  titulo: string
  corpo: string | null
  link: string | null
  lida_em: string | null
  created_at: string
}

export const CATEGORIA_TAREFA_LABEL: Record<CategoriaTarefa, string> = {
  analisar_exame: 'Analisar exame',
  responder_paciente: 'Responder paciente',
  concluir_evolucao: 'Concluir evolução',
  revisar_documento: 'Revisar documento',
  emitir_relatorio: 'Emitir relatório',
  verificar_resultado: 'Verificar resultado',
  autorizar_receita: 'Autorizar receita',
  definir_conduta: 'Definir conduta',
  acompanhar_tratamento: 'Acompanhar tratamento',
  solicitar_contato: 'Solicitar contato',
  agendar_retorno: 'Agendar retorno',
  conferir_documentacao: 'Conferir documentação',
  revisar_termo: 'Revisar termo',
  acompanhar_sintoma: 'Acompanhar sintoma',
  administrativa: 'Tarefa administrativa',
  personalizada: 'Tarefa personalizada',
}

export const PRIORIDADE_LABEL: Record<PrioridadeTarefa, string> = {
  baixa: 'Baixa',
  normal: 'Normal',
  alta: 'Alta',
  urgente: 'Urgente',
}

export const STATUS_TAREFA_LABEL: Record<StatusTarefa, string> = {
  nova: 'Nova',
  em_analise: 'Em análise',
  aguardando_medico: 'Aguardando médico',
  aguardando_secretaria: 'Aguardando secretária',
  aguardando_paciente: 'Aguardando paciente',
  aguardando_exame: 'Aguardando exame',
  programada: 'Programada',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
}

export const STATUS_TAREFA_ABERTOS: StatusTarefa[] = [
  'nova', 'em_analise', 'aguardando_medico', 'aguardando_secretaria',
  'aguardando_paciente', 'aguardando_exame', 'programada',
]

// ---------- Fase 4: protocolos, pré-anamnese, preparação ----------

export type TipoCampoPergunta =
  | 'texto_curto' | 'texto_longo' | 'numero' | 'data' | 'selecao_unica'
  | 'multipla_selecao' | 'sim_nao' | 'escala' | 'arquivo' | 'imagem'
  | 'documento' | 'assinatura' | 'confirmacao'

export interface Pergunta {
  id: string
  tipo: TipoCampoPergunta
  rotulo: string
  obrigatoria: boolean
  opcoes?: string[]
  /** lógica condicional: só aparece se a pergunta referida tiver esse valor */
  condicao?: { pergunta_id: string; valor: string }
}

export type TipoItemProtocolo = 'questionario' | 'orientacao' | 'checklist'

export interface ProtocoloItem {
  id: string
  tipo: TipoItemProtocolo
  titulo: string
  obrigatorio: boolean
  bloqueia: boolean
  texto?: string
  perguntas?: Pergunta[]
}

export interface Protocolo {
  id: string
  workspace_id: string
  nome: string
  descricao: string | null
  tipo_consulta: 'primeira' | 'retorno' | 'ambas'
  especialidade: string | null
  clinica_id: string | null
  ativo: boolean
}

export interface ProtocoloVersao {
  id: string
  protocolo_id: string
  numero: number
  publicada: boolean
  itens: ProtocoloItem[]
}

export interface PreparacaoItem {
  id: string
  preparacao_id: string
  item_ref: string
  tipo: TipoItemProtocolo
  titulo: string
  obrigatorio: boolean
  bloqueia: boolean
  status: 'pendente' | 'concluido' | 'dispensado'
  respostas: Record<string, unknown> | null
  respondido_por: string | null
  dispensado_motivo: string | null
  concluido_em: string | null
  created_at: string
}

export const TIPO_CAMPO_LABEL: Record<TipoCampoPergunta, string> = {
  texto_curto: 'Texto curto',
  texto_longo: 'Texto longo',
  numero: 'Número',
  data: 'Data',
  selecao_unica: 'Seleção única',
  multipla_selecao: 'Múltipla seleção',
  sim_nao: 'Sim ou não',
  escala: 'Escala (0 a 10)',
  arquivo: 'Arquivo',
  imagem: 'Imagem',
  documento: 'Documento',
  assinatura: 'Assinatura',
  confirmacao: 'Confirmação',
}

export const TIPO_ITEM_LABEL: Record<TipoItemProtocolo, string> = {
  questionario: 'Questionário',
  orientacao: 'Orientação',
  checklist: 'Checklist',
}
